import { useEffect, useState } from 'react';
import { useDeleteRequisitesStore, useRequisitesStore } from '@lib/store';
import { RecipientTypeEnum, type GetRequisitesResponse } from '@shared/api';
import { RecipientItem } from './recipientItem';
import { Alert, Button, Input, Modal } from '@shared/widgets';

interface SelectRecipientModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedRecipient?: GetRequisitesResponse;
  onChangeRecipient: (recipient: GetRequisitesResponse | undefined) => void;
  onChangeRecipientType: (type: RecipientTypeEnum) => void;
  onShowAddNewRecipientModal: () => void;
  onShowExternalPaymentModal: () => void;
}

export const SelectRecipientModal = (props: SelectRecipientModalProps) => {
  const requisitesStore = useRequisitesStore();
  const deleteRequisitesStore = useDeleteRequisitesStore();

  const [isOpenAlert, setIsOpenAlert] = useState<boolean>(false);
  const [selectedRecipient, setSelectedRecipient] = useState<GetRequisitesResponse | undefined>();
  const [requisiteToDelete, setRequisiteToDelete] = useState<GetRequisitesResponse>();
  const [search, setSearch] = useState<string>('');
  const [items, setItems] = useState<GetRequisitesResponse[] | null>(null);

  useEffect(() => {
    requisitesStore.getRequisites();
  }, [props.isOpen]);

  useEffect(() => {
    if (!requisitesStore.data) return setItems(null);
    if (search) {
      const searchUpper = search.toUpperCase();
      setItems(requisitesStore.data.filter((item) => item.OwnerName && item.OwnerName.toUpperCase().includes(searchUpper)));
    } else setItems(null);
  }, [search, requisitesStore.data]);

  const addNewRecipient = () => {
    props.onShowAddNewRecipientModal();
    props.onClose();
    props.onChangeRecipientType(RecipientTypeEnum.Add);
  };

  const editRecipient = () => {
    props.onShowAddNewRecipientModal();
    props.onClose();
    props.onChangeRecipientType(RecipientTypeEnum.Show);
  };

  const handleSelectRecipient = (requisite: GetRequisitesResponse) => {
    props.onChangeRecipient(requisite);
    setSelectedRecipient(requisite);
  };

  const handleBackButtonClick = () => {
    props.onShowExternalPaymentModal();
    props.onClose();
  };

  const handleDeleteAlert = (requisite: GetRequisitesResponse) => {
    setRequisiteToDelete(requisite);
    setIsOpenAlert(true);
  };

  const handleDeleteAlertConfirm = async () => {
    if (requisiteToDelete?.Id) {
      await deleteRequisitesStore.deleteRequisites(requisiteToDelete?.Id);
      setIsOpenAlert(false);
    }
    if (selectedRecipient?.Id === requisiteToDelete!.Id) {
      setSelectedRecipient(undefined);
      props.onChangeRecipient(undefined);
    }
    requisitesStore.getRequisites();
  };

  const handleDoneClick = () => {
    props.onShowExternalPaymentModal();
    props.onClose();
  };

  const displayedItems = items || requisitesStore.data;
  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose} title='PLEASE SELECT OR ADD NEW RECIPIENT'>
      <div className='w-full'>
        <div className='w-full'>
          <div className='w-full text-center text-sm font-semibold text-[#666] uppercase'>Select Recipient</div>
          <Input variant='outlined' rounded name='Search Recipient' placeholder='SEARCH' value={search} onChange={setSearch} />
          {displayedItems.length || requisitesStore.data.length ? (
            <div className='custom-scroll mt-4 -mr-8 mb-9 ml-0 h-[280px] w-[356px] overflow-y-auto'>
              <div className='mr-4'>
                {!displayedItems.length && requisitesStore.data.length && search ? (
                  <div className='w-full text-center text-sm font-semibold text-[#666] uppercase'>{`No results for: "${search}"`}</div>
                ) : null}
                {displayedItems.map((requisite, index) => (
                  <div key={index} className='border-t border-t-[#97815e33]'>
                    <RecipientItem
                      item={requisite}
                      query={search}
                      isActive={requisite.Id === props.selectedRecipient?.Id}
                      onSelect={handleSelectRecipient}
                      onDelete={handleDeleteAlert}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className='my-7 text-center text-xs font-semibold text-[#666] uppercase'>
              Your recipient list is empty, you can add them by clicking the button "Add new recipient"
            </div>
          )}
        </div>
        <Button fullWidth color='primary' className='mt-2' onClick={addNewRecipient}>
          Add new recipient
        </Button>
        {selectedRecipient && (
          <Button fullWidth color='primary' className='mt-2' onClick={editRecipient}>
            Show Details
          </Button>
        )}
        <div className='mt-4 flex w-full flex-row items-center justify-between gap-4'>
          <Button fullWidth color='outlined' className='button new-recipient-btn' onClick={handleBackButtonClick}>
            Back
          </Button>
          <Button fullWidth color='primary' disabled={selectedRecipient ? false : true} onClick={handleDoneClick}>
            Done
          </Button>
        </div>
        <Alert title='Removing The Recipient' isOpen={isOpenAlert} onClose={() => setIsOpenAlert(false)} onDelete={handleDeleteAlertConfirm}>
          Are you sure you want to remove a recipient «${requisiteToDelete?.OwnerName}»?
        </Alert>
      </div>
    </Modal>
  );
};
